import React, { useEffect, useState } from 'react';
import { fetchAmsLookup } from '../actions/lookupActions';
import { useDispatch, useSelector } from 'react-redux';
import { sortBy, filter } from 'lodash';
import { Link } from 'react-router';
import { Button, Header, Icon, Menu, Modal } from 'semantic-ui-react';

// Import util.
import enforceRole from '../utils/EnforceRole';

// Import config.
import { acl } from '../config';

function MenuNavBar({ location, roles }) {
  const dispatch = useDispatch();
  const items = useSelector(state => state.lookups.amsLookups.AmsNavigation);
  const [state, setState] = useState({ activeItem: null });

  // "2026 Data Coming Soon" prompt shown when the (AnalyticsUser-only) Dashboard
  // launchpad link is clicked. The Dashboard link points to the external BI
  // Tableau launchpad; until 2026 data is published we intercept the click and
  // surface this notice instead of navigating away.
  const [showDashboardModal, setShowDashboardModal] = useState(false);

  const activeItems = sortBy(
    filter(items, { parent: false, active: true }).map(e => {
      return { ...e.value };
    }),
    [
      function(o) {
        return o.weight;
      },
    ]
  );

  useEffect(() => {
    dispatch(fetchAmsLookup('AmsNavigation'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setState({
      activeItem:
        activeItems.find(
          e =>
            e.value === location.pathname ||
            (location.pathname.includes(e.value) && e.value !== '/')
        ) || null,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  useEffect(() => {
    if (location.pathname.indexOf('/help') === 0) {
      return;
    }
    if (
      location.pathname.includes('reviews') &&
      !location.pathname.includes('myregionalreviews')
    ) {
      if (
        state.activeItem &&
        state.activeItem.label &&
        state.activeItem.label !== 'Reports'
      )
        setState({
          activeItem: activeItems.find(e => e.label === 'Reports'),
        });
      return;
    }

    if (
      state.activeItem &&
      state.activeItem.value !== location.pathname &&
      (!location.pathname.includes(state.activeItem.value) ||
        state.activeItem.value === '/')
    )
      setState({
        activeItem:
          activeItems.find(
            e =>
              e.value === location.pathname ||
              (location.pathname.includes(e.value) && e.value !== '/')
          ) || null,
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  const handleItemClick = (e, { name }) =>
    setState({
      activeItem: activeItems.find(e => e.label === name),
      toPath: e.value,
    });

  // The Dashboard nav item routes to the external BI launchpad
  // (https://bi.ams20.com/#/views/Launchpad/DashboardLauchpad). Match it by
  // label or BI host so the notice fires regardless of the exact lookup value.
  const isDashboardLink = item =>
    (item.label && item.label.trim().toLowerCase() === 'dashboard') ||
    (item.value && item.value.toLowerCase().includes('bi.ams20.com'));

  const handleDashboardClick = event => {
    // Intercept navigation to the BI launchpad and show the "coming soon" notice.
    if (event) event.preventDefault();
    setShowDashboardModal(true);
  };

  const renderMainMenu = () => {
    return activeItems.map((e, key) => {
      if (!e.value) {
        return null;
      }
      const routeRoles =
        acl.routes[
          `${e.value
            .substring(1)
            .toLocaleLowerCase()
            .replace(/\s/g, '')}`
        ] || acl.routes[`${e.label.toLocaleLowerCase()}`];
      return enforceRole(
        <Menu.Item
          key={key}
          to={`${e.value}`}
          name={e.label}
          target={e.target}
          rel={e.target !== '' ? 'noopener noreferrer' : ''}
          onClick={isDashboardLink(e) ? handleDashboardClick : handleItemClick}
          active={state.activeItem ? state.activeItem.label === e.label : false}
          as={Link}
        >
          {e.label}
        </Menu.Item>,
        routeRoles,
        roles
      );
    });
  };

  return (
    <>
      {renderMainMenu()}
      <Menu.Menu position="right">
        <Menu.Item
          icon="graduation cap"
          content="Training"
          as={Link}
          to="/profile#training"
          active={
            location.pathname.indexOf('/training') === 0 ||
            (location.pathname === '/profile' &&
              location.hash.indexOf('#training') === 0)
          }
        />
        <Menu.Item
          color="green"
          icon="question circle"
          content="Help"
          as={Link}
          href="/help"
        />

        <Menu.Item
          as={Link}
          to="/calendar"
          name="calendar"
          onClick={() =>
            setState({
              activeItem: { name: 'calendar' },
              toPath: '/calendar',
            })
          }
          active={
            state.activeItem ? state.activeItem.label === 'calendar' : false
          }
        >
          <Icon name="calendar" />
          Calendar
        </Menu.Item>
      </Menu.Menu>

      {/*
        AnalyticsUser-only "2026 Data Coming Soon" prompt.
        Triggered by clicking the guarded Dashboard launchpad link above. The
        Modal portals to <body>, so rendering it here is layout-safe and it
        renders nothing inline while closed.
        `ams-semantic-modal-fix` sizes the modal to its content (fixes the
        legacy full-viewport-height modal issue used app-wide).
      */}
      <Modal
        open={showDashboardModal}
        onClose={() => setShowDashboardModal(false)}
        size="tiny"
        closeIcon={
          // Custom close icon positioned INSIDE the modal container. Semantic's
          // default closeIcon uses negative top/right offsets (sits outside,
          // above the box); ams-semantic-modal-fix unsets those, so we pin it
          // to the top-right corner within the modal instead.
          <Icon
            name="close"
            link
            aria-label="Close"
            style={{
              position: 'absolute',
              top: '0.75rem',
              right: '0.75rem',
              margin: 0,
              color: 'rgba(0, 0, 0, 0.6)',
            }}
          />
        }
        className="ams-semantic-modal-fix"
        role="alertdialog"
        aria-labelledby="dashboard-2026-modal-header"
      >
        <Header id="dashboard-2026-modal-header">
          <Icon name="calendar alternate outline" />
          <Header.Content>2026 Data Coming Soon</Header.Content>
        </Header>
        <Modal.Content>
          <p>
            The 2026 dashboard is not yet available and will be published in a
            future update. Please check back soon.
          </p>
        </Modal.Content>
        <Modal.Actions>
          <Button primary onClick={() => setShowDashboardModal(false)}>
            Got it
          </Button>
        </Modal.Actions>
      </Modal>
    </>
  );
}

const menuStyle = {
  border: 'none',
  borderRadius: 0,
  boxShadow: 'none',
  transition: 'box-shadow 0.5s ease, padding 0.5s ease',
  borderBottom: '2px solid rgb(229,229,229)',
};

const fixedMenuStyle = {
  backgroundColor: '#fff',
  border: '1px solid #ddd',
  boxShadow: '0px 3px 5px rgba(0, 0, 0, 0.2)',
};

const MemoMenuNavBar = React.memo(MenuNavBar);

export { menuStyle, fixedMenuStyle, MemoMenuNavBar };
